import { useMemo } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FlagIcon from '@/components/FlagIcon';
import { Menu, cx, focusRing } from '@/components/ui';
import type { MenuItem } from '@/components/ui';
import type { SupportedLanguage } from '@/i18n/language';
import { useAppLanguage } from '@/i18n/useAppLanguage';
import { playClickSound } from '@/utils/soundUtils';

type Props = {
  compact?: boolean;
  className?: string;
};

type LanguageOption = {
  lang: SupportedLanguage;
  labelKey: string;
  fallback: string;
  countryCode: string;
};

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { lang: 'en', labelKey: 'settings.english', fallback: 'English', countryCode: 'US' },
  { lang: 'es', labelKey: 'settings.spanish', fallback: 'Español', countryCode: 'ES' },
];

/**
 * Header language picker: a flag chip that opens a `Menu` on the raised surface.
 * Phones show the flag only; the name and chevron appear from `md` up.
 */
export default function LanguageSwitcher({ compact = false, className = '' }: Props) {
  const { t } = useTranslation();
  /** Menu plays the click sound and closes itself on select. */
  const { language: lang, setLanguage: apply } = useAppLanguage();

  const current = useMemo(
    () => LANGUAGE_OPTIONS.find((o) => o.lang === lang) ?? LANGUAGE_OPTIONS[0],
    [lang],
  );

  const currentLabel = t(current.labelKey, current.fallback);

  const items: MenuItem[] = LANGUAGE_OPTIONS.map((opt) => ({
    id: opt.lang,
    // Wrapped so the menu's icon slot keeps the flag at its own 3:2 size.
    icon: (
      <span className="inline-flex">
        <FlagIcon countryCode={opt.countryCode} size="md" />
      </span>
    ),
    label: t(opt.labelKey, opt.fallback),
    badge: lang === opt.lang ? <Check className="size-4 shrink-0 text-link" aria-hidden /> : undefined,
    onSelect: () => apply(opt.lang),
  }));

  return (
    <div data-tour-id="header.language" className={cx('relative shrink-0', className)}>
      <Menu
        aria-label={t('header.selectLanguage', 'Select language')}
        align="end"
        items={items}
        trigger={({ onClick, ...triggerProps }) => (
          <button
            type="button"
            {...triggerProps}
            onClick={() => {
              playClickSound();
              onClick();
            }}
            aria-label={t('header.changeLanguage', 'Change language, current: {{language}}', {
              language: currentLabel,
            })}
            className={cx(
              'inline-flex h-10 w-10 items-center justify-center rounded-control border border-edge-subtle bg-action-neutral',
              'font-ui text-small font-semibold text-primary transition-colors duration-(--k-dur-fast)',
              'hover:border-edge hover:bg-action-neutral-hover @bp-md:w-auto @bp-md:px-2.5',
              compact ? '@bp-md:gap-1' : '@bp-md:gap-1.5',
              focusRing,
            )}
          >
            <FlagIcon countryCode={current.countryCode} size={compact ? 'md' : 'lg'} />
            <span className="hidden max-w-18 min-w-0 truncate @bp-md:inline">{currentLabel}</span>
            <ChevronDown
              className={cx(
                'hidden size-3.5 shrink-0 text-tertiary transition-transform @bp-md:block',
                triggerProps['aria-expanded'] && 'rotate-180',
              )}
              aria-hidden
            />
          </button>
        )}
      />
    </div>
  );
}
